import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createCareer, advanceRound, userTeam, saveCareer, loadCareer, deleteCareer,
  careerSummary, startNextSeason, serializeCareer, configOf, economyOf, beginDraft,
  startUserMatch, finishUserMatch, SAVE_KEY_DESAFIO,
} from '../engine/career.js';
import { payrollOf, money } from '../engine/economy.js';
import { Toasts, useToasts, Stat } from './ui.jsx';
import { Home, LeaguePicker, ClubPicker } from './home.jsx';
import { MatchLive } from './match-live.jsx';
import { HowTo } from './howto.jsx';
import { Premium } from './premium.jsx';
import { readAccount, writeAccount, fetchAccount, votarEnquete, aoEncerrarSessao } from './api.js';
import { verTela, marcar } from './analytics.js';
import { vigiarVersao } from './versao.js';
import { HelpButton, HelpModal, UserMenu } from './modals.jsx';
import { HELP, telaJaVista, marcarTelaVista } from './help-content.js';
import { AccountModal } from './account-modal.jsx';
import { BoasVindas, jaViuBoasVindas, marcarBoasVindas } from './boas-vindas.jsx';
import { Flag } from './flags.jsx';
import { crestStyle } from '../data/crests.js';
import { isLeagueFree, applyPublishedConfig } from './premium-config.js';
import { fetchGameConfig } from './api.js';
import { Painel, Elenco, Mercado, Classificacao, Playoffs } from './screens.jsx';
import { Partidas } from './games-screen.jsx';
import { Gallery } from './gallery.jsx';
import { LineupScreen } from './lineup-screen.jsx';
import { CloudPanel } from './cloud-panel.jsx';
import { CeoPanel } from './ceo-panel.jsx';
import { pushSoon } from './cloud.js';
import { DesafioConvite, DesafioResultado, desafioTerminou } from './desafio-screen.jsx';
import { BugModal } from './bug-modal.jsx';
import { ouvirErros } from './diagnostico.js';

const TABS = [
  { id: 'painel', ic: '🏀', label: 'Painel' },
  { id: 'elenco', ic: '👥', label: 'Elenco' },
  { id: 'escalacao', ic: '📋', label: 'Tatica' },
  { id: 'mercado', ic: '💱', label: 'Mercado' },
  { id: 'tabela', ic: '📊', label: 'Tabela' },
  { id: 'jogos', ic: '📅', label: 'Jogos' },
  { id: 'galeria', ic: '🏆', label: 'Trofeus' },
];

export default function App() {
  const [state, setState] = useState(null);
  const [saved, setSaved] = useState(() => loadCareer());
  /* O desafio da semana e uma carreira curta que vive em OUTRA chave. Ele nunca
     encosta em `saved` -- entrar no desafio nao pode custar a carreira de
     ninguem. Ver SAVE_KEY_DESAFIO. */
  const [desafioSalvo, setDesafioSalvo] = useState(() => loadCareer(SAVE_KEY_DESAFIO));
  // 'home' -> 'liga' -> 'clube' -> jogo
  const [screen, setScreen] = useState('home');
  const [leagueId, setLeagueId] = useState(null);
  const [account, setAccount] = useState(() => readAccount());
  const [premium, setPremium] = useState(() => !!readAccount()?.premium);
  const [tab, setTab] = useState('painel');
  const [tick, setTick] = useState(0);
  const [toasts, pushToast] = useToasts();
  const [busy, setBusy] = useState(false);
  const [liveBox, setLiveBox] = useState(null);
  const [helpOpen, setHelpOpen] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [telaInicial, setTelaInicial] = useState(null);
  /* Só existe para redesenhar quando a configuração publicada chega.
     `applyPublishedConfig` MUTA o objeto PREMIUM, e o React não observa
     mutação: a tela já tinha desenhado o preço antigo e ficava com ele.
     Medido em 04/09/2026: o painel publicou R$ 14,99 e a home seguiu
     mostrando R$ 19,90 até limpar o cache. */
  const [, setConfigPronta] = useState(false);
  const [enquete, setEnquete] = useState(null);
  const [versaoNova, setVersaoNova] = useState(false);
  /* Boas-vindas: uma vez por aparelho, e so para quem ainda nao tem carreira.
     Quem ja esta jogando nao precisa que expliquem o jogo. */
  const [boasVindas, setBoasVindas] = useState(
    () => !jaViuBoasVindas() && !loadCareer(),
  );
  const [userMenu, setUserMenu] = useState(false);
  const [bugAberto, setBugAberto] = useState(false);
  /* A sessao foi derrubada por um login em outro aparelho. Vira um recado fixo
     na home, e nao um toast -- ver o comentario em `aoEncerrarSessao`. */
  const [sessaoEncerrada, setSessaoEncerrada] = useState(false);
  const [contaModal, setContaModal] = useState(null);
  const saveWarned = useRef(false);

  /**
   * Todo caminho de escrita passa por aqui: roda a acao do motor, mostra os
   * avisos e salva. A UI nunca mexe no estado por fora -- foi assim que o
   * projeto anterior acumulou cinco caminhos diferentes de boot.
   */
  const apply = (fn, { silent } = {}) => {
    if (!state) return;
    const result = fn(state);
    // `silent` existe por causa da partida ao vivo: mostrar o aviso de
    // "Vitoria 120 a 107" antes de a bola subir entrega o resultado e mata o
    // jogo. O aviso volta quando a partida termina.
    const events = silent ? result?.events?.filter((e) => !silent(e)) : result?.events;
    if (events?.length) pushToast(events);
    setTick((t) => t + 1);
    /* ⚠️ O desafio grava na chave dele e NAO vai para a nuvem. A nuvem guarda
       a carreira; mandar o desafio para la sobrescreveria a carreira de quem
       e premium com uma partida de dez rodadas. */
    const ehDesafio = !!state.desafio;
    const saved = saveCareer(state, ehDesafio ? SAVE_KEY_DESAFIO : undefined);
    if (!ehDesafio) pushSoon(JSON.parse(serializeCareer(state)));
    if (!saved.ok && !saveWarned.current) {
      saveWarned.current = true;
      pushToast({ kind: 'error', text: `Nao consegui salvar: ${saved.error}. Libere espaco no navegador.` });
    }
    return result;
  };

  const onCreate = ({ league, clubId, coachName }) => {
    const fresh = createCareer({ league, clubId, coachName });
    marcar('carreira_criada', { liga: league, clube: clubId });
    setState(fresh);
    setSaved(fresh);
    saveCareer(fresh);
    setTab('painel');
    setScreen('jogo');
    pushToast({ kind: 'win', text: `Bem-vindo ao ${userTeam(fresh).name}.` });
  };

  const onAdvance = (times = 1) => {
    if (busy) return;
    setBusy(true);
    // Cede um quadro antes de simular: sem isso o botao trava sem feedback e o
    // usuario acha que o clique nao pegou.
    setTimeout(() => {
      for (let i = 0; i < times; i++) {
        if (state.phase === 'offseason') break;
        const antes = state.offers.length;
        apply((s) => advanceRound(s));
        /* ⚠️ PARA quando chega proposta por um atleta seu.
           As propostas valem 5 rodadas; num "+20" elas nasciam e morriam entre
           dois cliques, e a pessoa terminava a temporada dizendo -- com razão
           -- que nunca recebeu nenhuma. Avançar em lote é para atravessar o
           que não exige decisão; proposta exige. */
        if (state.offers.length > antes) {
          pushToast({
            kind: 'offer',
            text: 'Chegou proposta por um atleta seu. Parei aqui para você decidir.',
          });
          break;
        }
      }
      setBusy(false);
    }, 16);
  };

  // Preco, ligas gratis e o recado do dono vem do servidor. Se ele nao
  // responder, o jogo abre com os valores locais -- nada fica esperando a rede.
  useEffect(() => {
    fetchGameConfig(readAccount()?.token)
      .then((cfg) => {
        applyPublishedConfig(cfg);
        setConfigPronta(true);
        setTelaInicial(cfg.telaInicial || null);
        setEnquete(cfg.enquete || null);
        if (cfg.aviso) {
          const visto = (() => { try { return localStorage.getItem('basquete82.aviso'); } catch { return null; } })();
          if (visto !== cfg.aviso.at) setAviso(cfg.aviso);
        }
      })
      .catch(() => { /* sem servidor: segue com o que ja esta no cliente */ });
  }, []);

  // Quem e o dono quem diz e o servidor, a cada boot. O que esta gravado no
  // aparelho e so lembranca: mudar o valor na mao abre o botao e nada mais --
  // as rotas /api/ceo/* conferem o e-mail do token e devolvem 404 para o resto.
  useEffect(() => {
    const conta = readAccount();
    if (!conta?.token) return;
    fetchAccount(conta.token)
      .then((me) => {
        const next = { ...conta, premium: !!me.premium, ceo: !!me.ceo };
        setAccount(writeAccount(next));
        setPremium(next.premium);
      })
      .catch(() => { /* offline ou token vencido: segue com o que ja estava */ });
  }, []);

  const onVotar = async (i) => {
    const conta = readAccount();
    if (!conta?.token) { pushToast({ kind: 'error', text: 'Entre na conta para votar.' }); return; }
    const antes = enquete;
    setEnquete({ ...enquete, meuVoto: i });
    try { await votarEnquete(conta.token, i); }
    catch (err) { setEnquete(antes); pushToast({ kind: 'error', text: err.message }); }
  };

  // Qual tela contar para o Analytics. Sem isto o GA veria uma visualizacao
  // por sessao e nunca diria onde as pessoas param -- a pagina e uma so.
  useEffect(() => {
    const atual = liveBox ? 'aovivo' : !state ? screen : tab;
    verTela(atual);

    /* A ajuda abre SOZINHA na primeira vez que a pessoa chega numa tela.
       Só onde existe texto de ajuda, e nunca por cima da partida ao vivo --
       um modal no meio do jogo interrompe o que a pessoa veio fazer. */
    if (atual && atual !== 'aovivo' && HELP[atual] && !telaJaVista(atual)) {
      marcarTelaVista(atual);
      setHelpOpen(atual);
    }
  }, [screen, tab, state, liveBox]);

  // Uma aba aberta ha dias nao recebe publicacao nenhuma. Isto avisa --
  // e SO avisa: quem recarrega e o jogador, quando quiser.
  /* Escuta os erros do navegador desde a subida. Sem isto o relato chega
     sem a unica informacao que costuma resolver sozinha: qual foi o erro. */
  useEffect(() => { ouvirErros(); }, []);

  /* ATUALIZAR SOZINHO, sem atropelar ninguem.
     O ref e atualizado a cada render e lido pelo vigia -- uma dependencia no
     useEffect remontaria o vigia a cada toque na tela, e cada remontagem
     dispara uma checagem: o servidor levaria uma rajada de pedidos por
     partida jogada.
     ⚠️ Partida ao vivo NAO pode ser interrompida: ela so entra na campanha
     quando termina, entao recarregar no meio dela apaga o jogo em andamento.
     Modal aberto tambem espera -- ninguem tem a tela sumindo no meio de um
     relato de problema ou de um login. */
  const ocupado = useRef(false);
  ocupado.current = !!liveBox || busy || !!bugAberto || !!contaModal
    || !!helpOpen || userMenu || boasVindas;

  useEffect(() => vigiarVersao(
    () => setVersaoNova(true),
    () => ocupado.current,
  ), []);

  /* UMA SESSAO POR CONTA: entrar em outro aparelho derruba esta.
     O `api.js` limpa a conta guardada assim que qualquer chamada com token
     responde 401; aqui a tela acompanha. Sem isto o cabecalho continuaria
     mostrando o nome de quem ja nao esta mais logado, e todo botao de conta
     falharia sem explicar por que.
     ⚠️ A CARREIRA NAO E TOCADA. Ela e do aparelho, nao da conta -- perder a
     sessao nao pode custar dez temporadas de jogo. */
  useEffect(() => aoEncerrarSessao(() => {
    setAccount(null);
    setPremium(false);
    setUserMenu(false);
    setContaModal(null);
    /* ⚠️ RECADO FIXO, e nao toast. O toast dura 5 s e some -- e o caso mais
       comum e justamente o pior para ele: a pessoa abre o jogo ja derrubada,
       o aviso nasce durante o carregamento e apaga antes de ela olhar. Medido
       aqui em 07/09/2026: o toast nunca chegou a ser visto. Um recado que fica
       ate a pessoa entrar de novo nao depende de ela estar olhando na hora. */
    setSessaoEncerrada(true);
  }), []);

  const onNewSeason = () => apply((s) => startNextSeason(s));
  const onBeginDraft = () => apply((s) => beginDraft(s));

  /**
   * "Jogar": simula a rodada e ABRE a partida ao vivo. A simulacao acontece
   * antes -- a tela ao vivo apenas reproduz o que ja foi decidido, entao o
   * placar do fim e exatamente o da sumula.
   */
  /**
   * "Jogar": ABRE a partida sem simular. Quem conduz e a tela -- e por isso
   * que da para pausar e substituir no meio. O resto da rodada so acontece
   * quando a partida termina.
   */
  const onPlayLive = () => {
    if (busy) return;
    marcar('partida_ao_vivo');
    const live = startUserMatch(state);
    if (!live) { pushToast({ kind: 'error', text: 'Sem jogo para voce nesta rodada.' }); return; }
    setLiveBox(live);
  };

  /** Fim da partida conduzida: aplica o resultado e roda o resto da rodada. */
  const onLiveFinish = (live) => {
    apply((s) => finishUserMatch(s, live));
    setLiveBox(null);
  };

  const onReset = () => {
    if (!window.confirm('Apagar a carreira e comecar outra? Nao da para desfazer.')) return;
    deleteCareer();
    setState(null);
    setSaved(null);
    setScreen('home');
  };

  // Volta ao inicio sem perder nada: o save fica com o estado ATUAL, nao com
  // o que foi carregado no boot.
  const onHome = () => {
    if (state) { saveCareer(state); setSaved(state); }
    setScreen('home');
    setState(null);
  };

  // Hooks SEMPRE antes de qualquer return condicional: chamar um useMemo
  // depois de um `if (...) return ...` muda a ordem dos hooks entre renders e
  // o React derruba a tela inteira.
  const summary = useMemo(() => (state ? careerSummary(state) : null), [state, tick]);
  const saveInfo = useMemo(() => {
    if (!saved) return null;
    const team = saved.teams.find((t) => t.id === saved.clubId);
    const row = saved.standings[saved.clubId];
    return {
      teamName: team?.name || '-',
      leagueName: configOf(saved).name,
      season: saved.season,
      round: saved.round,
      phase: saved.phase,
      record: row ? `${row.wins}-${row.losses}` : '0-0',
    };
  }, [saved, tick]);

  // Um fragmento so com os modais globais, usado em TODOS os returns. Montar
  // em cada tela separadamente e como um caminho fica sem o modal -- foi
  // exatamente o que aconteceu com a escolha de liga.
  const modais = (
    <>
      {boasVindas && (
        <BoasVindas
          onComecar={() => { marcarBoasVindas(); setBoasVindas(false); setScreen('liga'); }}
          onFechar={() => { marcarBoasVindas(); setBoasVindas(false); }}
        />
      )}

      {versaoNova && (
        <button className="versao-nova" onClick={() => window.location.reload()}>
          <b>Nova versão disponível.</b> Toque para atualizar.
        </button>
      )}
      {helpOpen && <HelpModal screen={helpOpen} onClose={() => setHelpOpen(null)} />}
      {bugAberto && (
        <BugModal
          onClose={() => setBugAberto(false)}
          tela={liveBox ? `aovivo` : !state ? screen : tab}
          state={state || saved}
        />
      )}
      {userMenu && (
        <UserMenu
          resumo={saved ? {
            clube: saved.teams.find((t) => t.id === saved.clubId)?.name || "-",
            liga: configOf(saved).name,
            temporada: saved.season,
            campanha: (() => {
              const r = saved.standings?.[saved.clubId];
              return r ? `${r.wins}-${r.losses}` : "0-0";
            })(),
            titulos: (saved.history || []).filter((h) => (h.trophies || [h.trophy]).includes("liga")).length,
            trofeus: (saved.history || []).reduce(
              (n, h) => n + ((Array.isArray(h.trophies) && h.trophies.length) ? h.trophies.length : (h.trophy ? 1 : 0)), 0,
            ),
          } : null}
          account={account}
          premium={premium}
          onClose={() => setUserMenu(false)}
          onAccount={() => { setUserMenu(false); setContaModal('conta'); }}
          onPremium={() => { setUserMenu(false); setContaModal('desbloquear'); }}
          onHowTo={() => { setUserMenu(false); setState(null); setScreen('comojogar'); }}
          onCeo={account?.ceo ? () => { setUserMenu(false); setState(null); setScreen('ceo'); } : null}
          onLogout={() => {
            writeAccount(null); setAccount(null); setPremium(false); setUserMenu(false);
          }}
        />
      )}
      {aviso && (
        <div className="backdrop" onClick={(e) => { if (e.target === e.currentTarget) setAviso(null); }}>
          <div className="modal">
            <p className="kicker">• Recado</p>
            <h1 style={{ marginBottom: 6 }}>{aviso.titulo}</h1>
            <p className="muted" style={{ whiteSpace: 'pre-wrap' }}>{aviso.texto}</p>
            <button className="primary" style={{ width: '100%', marginTop: 12 }}
              onClick={() => {
                try { localStorage.setItem('basquete82.aviso', aviso.at); } catch { /* bloqueado */ }
                setAviso(null);
              }}>
              entendi
            </button>
          </div>
        </div>
      )}
      <AccountModal
        open={!!contaModal}
        abaInicial={contaModal}
        onClose={() => setContaModal(null)}
        account={account}
        setAccount={setAccount}
        premium={premium}
        onPremiumChange={setPremium}
        state={state}
        saved={saved}
        onAdopt={(remoto) => {
          setState(remoto); setSaved(remoto); saveCareer(remoto);
          pushToast({ kind: 'win', text: 'Carreira da nuvem carregada neste aparelho.' });
        }}
        onReset={onReset}
        onCeo={account?.ceo ? () => { setState(null); setScreen('ceo'); } : null}
        onLogout={() => {
          writeAccount(null); setAccount(null); setPremium(false); setContaModal(null);
        }}
      />
    </>
  );

  if (!state) {
    /* ⚠️ O RESULTADO VEM ANTES DE TUDO. Se o desafio terminou, a tela dele
       manda -- inclusive na frente do 'jogo', que senao mostraria uma rodada 35
       que nao existe no cenario de 10 rodadas. */
    /* ⚠️ A sessao pode cair COM a tela do desafio aberta (login em outro
       aparelho derruba esta). Sem esta guarda, a pessoa ficaria olhando um
       "Nao consegui falar com o servidor" que na verdade e um 401 -- erro de
       rede na aparencia, falta de conta na causa. Volta para a home e abre o
       cadastro, que e o que resolve. */
    if (screen === 'desafio' && !account) {
      setScreen('home');
      setUserMenu(true);
      return <>{modais}</>;
    }

    if (screen === 'desafio') {
      return (
        <>{modais}
          <DesafioConvite
            account={account}
            onVoltar={() => setScreen('home')}
            onJogar={(montado) => {
              setState(montado.estado);
              setDesafioSalvo(montado.estado);
              saveCareer(montado.estado, SAVE_KEY_DESAFIO);
              setTab('painel');
              setScreen('jogo');
              pushToast({ kind: 'info', text: `Voce assumiu o ${montado.clubeNome}. Boa sorte.` });
            }}
          />
        </>
      );
    }

    if (screen === 'ceo' && account?.ceo) {
      return <>{modais}<CeoPanel onBack={() => setScreen('home')} /></>;
    }
    if (screen === 'comojogar') {
      return <>{modais}<HowTo onBack={() => setScreen('home')} onNew={() => setScreen('liga')} /></>;
    }
    if (screen === 'premium') {
      return (
        <>{modais}
        <Premium
          onBack={() => setScreen('home')}
          account={account}
          setAccount={setAccount}
          onPremiumChange={setPremium}
        /></>
      );
    }
    if (screen === 'liga') {
      return (
        <>{modais}
        <LeaguePicker
          premium={premium}
          onPremium={() => setContaModal('desbloquear')}
          onPick={(id) => {
            // A trava do premium mora aqui e no servidor -- a tela so evita o
            // caminho, ela nao e a guarda.
            if (!isLeagueFree(id) && !premium) { setScreen('premium'); return; }
            setLeagueId(id);
            setScreen('clube');
          }}
          onBack={onHome}
        /></>
      );
    }
    if (screen === 'clube') {
      return (
        <>{modais}
        <ClubPicker leagueId={leagueId} onCreate={onCreate} onBack={() => setScreen('liga')} /></>
      );
    }
    return (
      <>
      <Home
        hasSave={!!saved}
        saveInfo={saveInfo}
        toasts={toasts}
        account={account}
        premium={premium}
        onContinue={() => { setState(saved); setScreen('jogo'); setTab('painel'); }}
        onNew={() => setScreen('liga')}
        onDelete={onReset}
        onHowTo={() => setScreen('comojogar')}
        onDesafio={account ? () => setScreen('desafio') : () => setUserMenu(true)}
        onBug={account ? () => setBugAberto(true) : null}
        sessaoEncerrada={sessaoEncerrada}
        onEntrar={() => { setSessaoEncerrada(false); setUserMenu(true); }}
        temporadasFechadas={saved?.history?.length || 0}
        desafioEmAndamento={!!desafioSalvo && !desafioTerminou(desafioSalvo)}
        onRetomarDesafio={() => {
          /* Retomar tambem pede conta: o desafio so vale se o resultado puder
             entrar no ranking, e o ranking e por conta. Deixar continuar sem
             login levaria a pessoa a jogar 10 rodadas e so descobrir no fim
             que nao pode enviar. */
          if (!account) { setUserMenu(true); return; }
          setState(desafioSalvo);
          setTab('painel');
          setScreen('jogo');
        }}
        onPremium={() => setContaModal('desbloquear')}
        onAccount={() => setUserMenu(true)}
        telaInicial={telaInicial}
        enquete={enquete}
        onVotar={onVotar}
      />
      {modais}
      </>
    );
  }

  const config = configOf(state);
  const offers = state.offers.length;

  /* ⚠️ O DESAFIO ACABOU? A tela de resultado toma o lugar do jogo.
     Sem esta guarda a pessoa continuaria avancando rodadas depois da 34a e o
     placar do desafio mudaria DEPOIS de ela ja ter mandado o resultado -- o
     ranking passaria a mentir. */
  if (state.desafio && desafioTerminou(state)) {
    return (
      <>{modais}
        <DesafioResultado
          state={state}
          account={account}
          onSair={() => {
            deleteCareer(SAVE_KEY_DESAFIO);
            setDesafioSalvo(null);
            setState(null);
            setScreen('desafio');
          }}
        />
      </>
    );
  }

  // A partida ao vivo toma a tela: nada de abas competindo com o jogo.
  if (liveBox) {
    return (
      <>
        <div className="topbar">
          <div className="inner">
            <div className="brand">ao vivo</div>
            <div className="meta">Temporada {state.season}</div>
          </div>
        </div>
        <MatchLive
          live={liveBox}
          config={config}
          userTeamId={state.clubId}
          onFinish={onLiveFinish}
        />
        <Toasts items={toasts} />
      </>
    );
  }

  return (
    <>
      <div className="topbar">
        <div className="inner">
          {/* Esquerda: so icones e o nome do clube. O e-mail inteiro no topo
              empurrava a barra e fazia a informacao da direita quebrar em
              quatro linhas -- ele agora vive no menu da conta. */}
          <div className="row" style={{ gap: 7, minWidth: 0 }}>
            <button className="icon-btn" onClick={onHome} title="Inicio" aria-label="Inicio">⌂</button>
            <button
              className={`avatar-btn ${premium ? 'pro' : ''}`}
              onClick={() => setUserMenu(true)}
              title={account ? account.email : 'Sua conta'}
              aria-label="Sua conta"
            >
              {(account?.email || 'V')[0].toUpperCase()}
            </button>
            <HelpButton screen={tab} onOpen={setHelpOpen} />
            {/* No celular a barra mostra a SIGLA; o nome inteiro so cabe em
                tela larga, e cortado com reticencias nao ajuda ninguem. */}
            <div className="topbar-club">
              <Flag code={config.flagCode} size={15} title={config.country} />
              <span className="abbr">{crestStyle(summary.team.name).abbr}</span>
              <span className="nm">{summary.team.name}</span>
            </div>
          </div>
          <div className="meta">
            <span className="money">{money(summary.team.money)}</span>
            <span className="when">
              {state.phase === 'regular' && `T${state.season} · rodada ${Math.min(state.round, config.regularRounds)}/${config.regularRounds}`}
              {state.phase === 'playoffs' && `T${state.season} · playoffs`}
              {state.phase === 'draft' && `T${state.season} · draft`}
              {state.phase === 'offseason' && `T${state.season} · entressafra`}
            </span>
          </div>
        </div>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>
            <span className="ic">{t.ic}</span>
            <span>{t.label}</span>
            {t.id === 'mercado' && offers > 0 && <span className="badge">{offers}</span>}
          </button>
        ))}
      </div>

      <div className="wrap">
        {tab === 'painel' && (
          <Painel
            state={state} summary={summary} busy={busy}
            onAdvance={onAdvance} onNewSeason={onNewSeason} onReset={onReset}
            onGo={setTab} onPlayLive={onPlayLive} onBeginDraft={onBeginDraft}
            apply={apply}
          />
        )}
        {tab === 'elenco' && <Elenco state={state} apply={apply} />}
        {tab === 'escalacao' && <LineupScreen state={state} apply={apply} />}
        {tab === 'mercado' && <Mercado state={state} apply={apply} />}
        {tab === 'tabela' && (state.phase === 'playoffs' || state.bracket
          ? <Playoffs state={state} /> : <Classificacao state={state} />)}
        {tab === 'jogos' && <Partidas state={state} />}
        {tab === 'galeria' && (
          <>
            <Gallery state={state} />
            <h2>Nuvem</h2>
            <CloudPanel
              state={state}
              premium={premium}
              onAdopt={(remoto) => {
                setState(remoto); setSaved(remoto); saveCareer(remoto);
                pushToast({ kind: 'win', text: 'Carreira da nuvem carregada neste aparelho.' });
              }}
            />
          </>
        )}
      </div>

      <Toasts items={toasts} />
      {modais}
    </>
  );
}

/** Barra de situacao do clube, usada no painel e no mercado. */
export function ClubBar({ state }) {
  const team = userTeam(state);
  const econ = economyOf(state);
  const payroll = payrollOf(team.roster);
  const over = payroll > econ.salaryCap;
  return (
    <div className="club-bar">
      <div>
        <div className="k">Caixa</div>
        <div className={`v ${team.money < econ.salaryCap * 0.04 ? 'neg' : ''}`}>{money(team.money)}</div>
      </div>
      <div>
        <div className="k">Folha</div>
        <div className={`v ${over ? 'warn' : 'pos'}`}>{money(payroll)}</div>
      </div>
      <div>
        <div className="k">Teto</div>
        <div className="v">{money(econ.salaryCap)}</div>
      </div>
      <div>
        <div className="k">Elenco</div>
        <div className={`v ${team.roster.length <= econ.minRoster ? 'warn' : ''}`}>
          {team.roster.length}/{econ.maxRoster}
        </div>
      </div>
    </div>
  );
}

export { serializeCareer };
